import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createPedidoCompra,
  getAllSuppliers,
  getPedidoCompra,
  updatePedidoCompra,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { getAuthPermissoes } from '../services/authStorage';
import { useEstabelecimentoFiltroApi } from '../context/EstabelecimentoContext';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import {
  processarBuscaProdutoEnter,
  PRODUCT_SEARCH_HINT,
} from '../utils/productSearch';

const money = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const num = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const nomeFornecedor = (f) => f?.nomeFantasia || f?.razaoSocial || (f?.id ? `Fornecedor ${f.id}` : '');

const custoCadastro = (produto) => {
  const precos = produto?.precos || produto?.Precos || [];
  const row = precos.find((p) => p.tipo === 3 || p.tipo === 'Custo' || p.Tipo === 3);
  return Number(row?.valor ?? row?.Valor ?? produto?.custo ?? produto?.valorCusto ?? 0) || 0;
};

const linhaTotal = (item, mostrarAtendida) => {
  const qtdAt = num(item.qtdAtendida);
  const base = mostrarAtendida && qtdAt > 0 ? qtdAt : num(item.qtdPedida);
  return Number((base * num(item.custoUnitario)).toFixed(2));
};

/** @param {{ isEditMode?: boolean }} props */
export default function FormPedidoCompra({ isEditMode = false }) {
  const { id } = useParams();
  const editing = isEditMode || Boolean(id);
  const navigate = useNavigate();
  const estabelecimentoContextoId = useEstabelecimentoFiltroApi();
  const permissions = useMemo(() => getAuthPermissoes() || {}, []);
  const podeUsar = Boolean(permissions.pedidoCompra);

  const [estabelecimentoId, setEstabelecimentoId] = useState(estabelecimentoContextoId || '');
  const [fornecedor, setFornecedor] = useState(null);
  const [fornecedorOpts, setFornecedorOpts] = useState([]);
  const [observacao, setObservacao] = useState('');
  const [itens, setItens] = useState([]);
  const [buscaProduto, setBuscaProduto] = useState('');
  const [escolha, setEscolha] = useState(null); // lista de produtos para escolher
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [codigo, setCodigo] = useState('');
  const [status, setStatus] = useState('Rascunho');

  useEffect(() => {
    if (!editing && estabelecimentoContextoId) setEstabelecimentoId(estabelecimentoContextoId);
  }, [editing, estabelecimentoContextoId]);

  const buscarFornecedores = useCallback(async (termo) => {
    try {
      const { data } = await getAllSuppliers({ indice: 1, qtde: 15, termo: (termo || '').trim() });
      setFornecedorOpts(data?.items || (Array.isArray(data) ? data : []));
    } catch {
      setFornecedorOpts([]);
    }
  }, []);

  useEffect(() => {
    buscarFornecedores('');
  }, [buscarFornecedores]);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await getPedidoCompra(id);
        setCodigo(data.codigo || '');
        setStatus(data.status || 'Rascunho');
        setEstabelecimentoId(data.estabelecimentoId || '');
        setFornecedor({
          id: data.fornecedorId,
          nomeFantasia: data.fornecedorNome,
          razaoSocial: data.fornecedorNome,
        });
        setObservacao(data.observacao || '');
        setItens(
          (data.itens || []).map((i) => ({
            id: i.id,
            produtoId: i.produtoId,
            produtoCodigo: i.produtoCodigo,
            produtoDescricao: i.produtoDescricao,
            qtdPedida: Number(i.qtdPedida || 0),
            qtdAtendida: Number(i.qtdAtendida || 0),
            custoUnitario: Number(i.custoUnitario || 0),
            observacao: i.observacao || '',
          })),
        );
      } catch (e) {
        setError(getApiErrorMessage(e, 'Erro ao carregar pedido de compra.'));
      } finally {
        setLoading(false);
      }
    })();
  }, [editing, id]);

  const isRascunho = !editing || status === 'Rascunho';
  const isRespondido = editing && status === 'Respondido';
  const readOnly = !isRascunho && !isRespondido;

  const total = useMemo(
    () => itens.reduce((s, i) => s + linhaTotal(i, isRespondido), 0),
    [itens, isRespondido],
  );

  const incluirProduto = (p, quantidade = 1) => {
    setItens((prev) => {
      const idx = prev.findIndex((i) => i.produtoId === p.id);
      if (idx >= 0) {
        return prev.map((i, k) => (
          k === idx ? { ...i, qtdPedida: num(i.qtdPedida) + Number(quantidade || 1) } : i
        ));
      }
      return [
        ...prev,
        {
          produtoId: p.id,
          produtoCodigo: p.codigo,
          produtoDescricao: p.descricao,
          qtdPedida: Number(quantidade || 1),
          qtdAtendida: 0,
          custoUnitario: custoCadastro(p),
          observacao: '',
        },
      ];
    });
    setBuscaProduto('');
  };

  const addProduto = async () => {
    setError('');
    if (!estabelecimentoId) {
      setError('Selecione o estabelecimento.');
      return;
    }
    try {
      const resultado = await processarBuscaProdutoEnter({
        termo: buscaProduto,
        estabelecimentoId: estabelecimentoId || undefined,
        ativo: true,
      });
      if (resultado.tipo === 'vazio') return;
      if (resultado.tipo === 'nao_encontrado') {
        setError(resultado.mensagem || 'Produto não encontrado.');
        return;
      }
      if (resultado.tipo === 'lista') {
        setEscolha(resultado.produtos || []);
        return;
      }
      incluirProduto(resultado.produto, resultado.quantidade);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Erro ao buscar produto.'));
    }
  };

  const updateItem = (idx, patch) =>
    setItens((prev) => prev.map((item, i) => (i === idx ? { ...item, ...patch } : item)));

  const handleSave = async () => {
    setError('');
    if (!estabelecimentoId) {
      setError('Selecione o estabelecimento.');
      return;
    }
    if (!fornecedor?.id) {
      setError('Selecione o fornecedor.');
      return;
    }
    if (itens.length === 0) {
      setError('Inclua ao menos um item.');
      return;
    }
    if (itens.some((i) => num(i.qtdPedida) <= 0)) {
      setError('A quantidade pedida deve ser maior que zero em todos os itens.');
      return;
    }
    if (itens.some((i) => num(i.custoUnitario) < 0 || (isRespondido && num(i.qtdAtendida) < 0))) {
      setError('Custo e quantidade atendida não podem ser negativos.');
      return;
    }
    setSaving(true);
    const payload = {
      estabelecimentoId: Number(estabelecimentoId),
      fornecedorId: Number(fornecedor.id),
      observacao: observacao || null,
      itens: itens.map((i) => ({
        ...(i.id ? { id: i.id } : {}),
        produtoId: i.produtoId,
        qtdPedida: num(i.qtdPedida),
        ...(isRespondido ? { qtdAtendida: num(i.qtdAtendida) } : {}),
        custoUnitario: num(i.custoUnitario),
        observacao: i.observacao || null,
      })),
    };
    try {
      if (editing) {
        await updatePedidoCompra(id, payload);
        navigate('/pedidos-compra');
      } else {
        await createPedidoCompra(payload);
        navigate('/pedidos-compra');
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'Erro ao salvar pedido de compra.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <PageHeader
        title={editing ? `${codigo || 'Pedido de compra'} · ${status}` : 'Novo pedido de compra'}
        subtitle={
          readOnly
            ? 'Pedido somente leitura neste status.'
            : isRespondido
              ? 'Resposta do fornecedor: ajuste custos e quantidades antes de aprovar.'
              : 'Monte a lista de itens, salve e gere o link para o fornecedor responder.'
        }
      />

      {!podeUsar && (
        <Alert severity="warning" sx={{ mb: 2 }}>Sem permissão para pedidos de compra.</Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 320, flex: 1 }}
            options={
              fornecedor && !fornecedorOpts.some((o) => o.id === fornecedor.id)
                ? [fornecedor, ...fornecedorOpts]
                : fornecedorOpts
            }
            value={fornecedor}
            disabled={!isRascunho}
            getOptionLabel={nomeFornecedor}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(x) => x}
            noOptionsText="Nenhum fornecedor"
            onInputChange={(_, v, reason) => {
              if (reason === 'input') buscarFornecedores(v);
            }}
            onChange={(_, v) => setFornecedor(v)}
            renderInput={(params) => <TextField {...params} label="Fornecedor" required />}
          />
          <Typography sx={{ alignSelf: 'center', fontWeight: 800 }}>
            Total {money(total)}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        {isRascunho && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField
              label="Produto"
              size="small"
              fullWidth
              value={buscaProduto}
              helperText={PRODUCT_SEARCH_HINT}
              onChange={(e) => setBuscaProduto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addProduto();
                }
              }}
            />
            <Button variant="contained" onClick={addProduto}>Incluir</Button>
          </Stack>
        )}

        <ResponsiveTable>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell align="right">Qtd pedida</TableCell>
                {(isRespondido || (readOnly && editing)) && (
                  <TableCell align="right">Qtd atendida</TableCell>
                )}
                <TableCell align="right">Custo unit.</TableCell>
                <TableCell>Observação</TableCell>
                <TableCell align="right">Total</TableCell>
                {isRascunho && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {itens.map((item, idx) => (
                <TableRow key={`${item.produtoId}-${idx}`}>
                  <TableCell data-label="Código">{item.produtoCodigo}</TableCell>
                  <TableCell data-label="Descrição">{item.produtoDescricao}</TableCell>
                  <TableCell data-label="Qtd pedida" align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={item.qtdPedida}
                      disabled={readOnly}
                      onChange={(e) => updateItem(idx, { qtdPedida: e.target.value })}
                      inputProps={{ min: 0, step: 'any' }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  {(isRespondido || (readOnly && editing)) && (
                    <TableCell data-label="Qtd atendida" align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={item.qtdAtendida}
                        disabled={!isRespondido}
                        onChange={(e) => updateItem(idx, { qtdAtendida: e.target.value })}
                        inputProps={{ min: 0, step: 'any' }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                  )}
                  <TableCell data-label="Custo unit." align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={item.custoUnitario}
                      disabled={readOnly}
                      onChange={(e) => updateItem(idx, { custoUnitario: e.target.value })}
                      inputProps={{ min: 0, step: 'any' }}
                      sx={{ width: 120 }}
                    />
                  </TableCell>
                  <TableCell data-label="Observação">
                    <TextField
                      size="small"
                      value={item.observacao}
                      disabled={readOnly}
                      onChange={(e) => updateItem(idx, { observacao: e.target.value })}
                      inputProps={{ maxLength: 300 }}
                      sx={{ minWidth: 160 }}
                    />
                  </TableCell>
                  <TableCell data-label="Total" align="right">
                    {money(linhaTotal(item, isRespondido || readOnly))}
                  </TableCell>
                  {isRascunho && (
                    <TableCell data-actions="" align="right">
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setItens((p) => p.filter((_, i) => i !== idx))}
                      >
                        Remover
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {itens.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                      Nenhum item.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ResponsiveTable>
      </Paper>

      <TextField
        label="Observação"
        fullWidth
        multiline
        minRows={2}
        value={observacao}
        disabled={readOnly}
        inputProps={{ maxLength: 500 }}
        onChange={(e) => setObservacao(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button variant="outlined" onClick={() => navigate('/pedidos-compra')}>
          Voltar
        </Button>
        {!readOnly && (
          <Button variant="contained" disabled={saving || !podeUsar} onClick={handleSave}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        )}
      </Stack>

      <Dialog open={Boolean(escolha)} onClose={() => setEscolha(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Vários produtos encontrados</DialogTitle>
        <DialogContent dividers>
          <List dense>
            {(escolha || []).map((p) => (
              <ListItemButton
                key={p.id}
                onClick={() => {
                  incluirProduto(p, 1);
                  setEscolha(null);
                }}
              >
                <ListItemText
                  primary={p.descricao}
                  secondary={[p.codigo, p.ean].filter(Boolean).join(' · ')}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEscolha(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
